/**
 * MobileCard — compact card view used on phones (< 768px) for Scanner and
 * Portfolio rows. Replaces the wide horizontally-scrolling tables which are
 * unusable on small screens.
 *
 * The card has three fixed slots:
 *   header  — symbol + sector + status badge / setup tags
 *   body    — 2-column grid of key/value pairs (price levels, R, P&L, etc.)
 *   actions — small row of action buttons (optional)
 *
 * Each consumer (Scanner / Portfolio) supplies its own slot contents.
 */
import React from 'react';

export default function MobileCard({ accent = '#333', children, onClick, style = {} }) {
  return (
    <div
      onClick={onClick}
      style={{
        background: '#1a1a1a',
        border: '1px solid #2a2a2a',
        borderLeft: `3px solid ${accent}`,
        borderRadius: 8,
        padding: '12px 14px',
        marginBottom: 10,
        fontSize: 13,
        cursor: onClick ? 'pointer' : 'default',
        ...style,
      }}
    >
      {children}
    </div>
  );
}

/** Compact key/value used inside the card body grid. */
export function MobileKV({ label, value, color = '#fff', mono = false, bold = false }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
      <span style={{ fontSize: 10, color: '#666', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        {label}
      </span>
      <span
        style={{
          fontSize: 13,
          color,
          fontWeight: bold ? 700 : 500,
          fontFamily: mono ? 'ui-monospace, monospace' : 'inherit',
          fontVariantNumeric: 'tabular-nums',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {value ?? '—'}
      </span>
    </div>
  );
}

/** Header row inside the card — symbol + meta. */
export function MobileCardHeader({ symbol, sector, right }) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: 10,
        marginBottom: 10,
        paddingBottom: 8,
        borderBottom: '1px solid #222',
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
        <span style={{ fontWeight: 700, fontFamily: 'ui-monospace, monospace', fontSize: 15 }}>
          {symbol}
        </span>
        {sector && (
          <span style={{ fontSize: 11, color: '#888' }}>{sector}</span>
        )}
      </div>
      {right && <div style={{ flexShrink: 0 }}>{right}</div>}
    </div>
  );
}

/** Grid container for MobileKV pairs inside the body. */
export function MobileCardBody({ children, cols = 2 }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${cols}, 1fr)`,
        gap: '10px 12px',
      }}
    >
      {children}
    </div>
  );
}

/** Footer row for action buttons. */
export function MobileCardActions({ children }) {
  return (
    <div
      style={{
        display: 'flex',
        gap: 6,
        marginTop: 12,
        paddingTop: 10,
        borderTop: '1px solid #222',
        flexWrap: 'wrap',
      }}
    >
      {children}
    </div>
  );
}
